/**
 * FilterUIStrategy (Abstract Base Class)
 *
 * Defines interface for all filter UI rendering strategies.
 * Implements Strategy pattern for adaptive UI rendering.
 *
 * SOLID Compliance:
 * - SRP: Only defines strategy interface
 * - OCP: Extensible (new strategies extend this)
 * - LSP: All derived classes must be substitutable
 * - ISP: Minimal interface (only essential methods)
 * - DIP: High-level code depends on this abstraction
 *
 * Concrete Strategies:
 * - DesktopDrawerStrategy: Desktop layout with inline fields + drawer
 * - MobileScrollStrategy: Mobile portrait with progressive disclosure
 * - MobileGridStrategy: Mobile landscape with 2x2 grid
 *
 * Usage:
 * class MyStrategy extends FilterUIStrategy {
 *   render(filters, urlStateManager, filterRepository) {
 *     return <div>My UI</div>;
 *   }
 * }
 *
 * Location: apps/web/src/components/search/strategies/FilterUIStrategy.js
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

class FilterUIStrategy {
  /**
   * Render filter UI for this strategy
   *
   * @param {Object} filters - Current filter state from URL
   * @param {URLStateManager} urlStateManager - State manager for updating filters
   * @param {Object} filterRepository - Filter configuration repository
   * @returns {JSX.Element} - Rendered UI component
   * @throws {Error} - If not implemented by subclass
   */
  render(_filters, _urlStateManager, _filterRepository) {
    throw new Error(
      `FilterUIStrategy.render() must be implemented by subclass ${this.constructor.name}`
    );
  }

  /**
   * Validate that required filters are present
   *
   * Optional method - subclasses can override for custom validation.
   * Default implementation checks if required filters from repository are present.
   *
   * @param {Object} filters - Current filter state
   * @param {Object} filterRepository - Filter configuration repository
   * @returns {Object} - { valid: boolean, _errors: Array<string> }
   */
  validate(filters, filterRepository) {
    const _errors = [];

    // Get required filters from repository
    const requiredFilters = Object.values(filterRepository).filter(
      f => f.requiredForSearch
    );

    // Check each required filter
    for (const filter of requiredFilters) {
      const filterValue = filters[filter.urlParam];

      if (!filterValue || filterValue === '') {
        _errors.push(`${filter.label} is required`);
      }
    }

    return {
      valid: _errors.length === 0,
      _errors
    };
  }

  /**
   * Get strategy name (for debugging/analytics)
   * @returns {string} - Strategy class name
   */
  getName() {
    return this.constructor.name;
  }

  /**
   * Get strategy type (viewport + orientation)
   * @returns {string} - Human-readable strategy type
   */
  getType() {
    // Default implementation - subclasses can override
    return 'unknown';
  }

  /**
   * Check if this strategy supports advanced filters
   * @returns {boolean}
   */
  supportsAdvancedFilters() {
    // Default: true for desktop strategies, false for mobile
    // Subclasses can override
    return true;
  }

  /**
   * Get number of visible primary filters
   * @returns {number}
   */
  getVisiblePrimaryFilterCount() {
    // Default: all primary filters visible
    // Subclasses can override (e.g., MobileScrollStrategy shows 2)
    return Number.POSITIVE_INFINITY;
  }
}

export default FilterUIStrategy;
